import api from './api'

export interface GeneratePoCRequest {
  noteType: 'poc'
  therapistSummary: string | null
  contractId: string
  sessionID: string
}

export interface GeneratePoCResponse {
  type: string
  generatedNote: string
}

export interface SavePoCRequest {
  contract_id: string
  session_id: string
  poc_status: 'draft' | 'submitted'
  poc_doc: string // HTML content
  signature?: string | null
}

export interface SavePoCResponse {
  message: string
  data: {
    _id: string
    contract_id: string
    session_id: string
    poc_doc: string
    poc_status: string
    authorization_status?: string
    createdAt: string
    updatedAt: string
    poc_submit_date?: string
    is_deleted?: boolean
    __v?: number
  }
}

export interface CertifyPoCRequest {
  poc_id: string
}

export interface CertifyPoCResponse {
  _id: string
  contract_id: string
  session_id: string
  poc_doc: string
  poc_status: string
  authorization_status: string
  createdAt: string
  updatedAt: string
}

export interface PoCDocument {
  _id: string
  contract_id: string
  session_id: string
  poc_doc: string
  poc_status: 'draft' | 'certified'
  authorization_status: 'pending' | 'submitted' | 'approved' | 'denied'
  is_deleted: boolean
  createdAt: string
  updatedAt: string
  poc_submit_date?: string
  certified_by?: { name?: string; id?: string }
  certified_license?: { licenseFileUrl?: string }
  certify_date?: string
  previous_poc_id?: string
  pre_auth_id?: string
  approved_date?: string
  authorized_no_session?: number
  auth_service_period?: string
  denial_date?: string
  denial_reason?: string
  auth_submit_date?: string
  pdf_url?: string
}

export interface GetPoCListResponse {
  message: string
  data: PoCDocument[]
}

export interface UpdatePoCStatusRequest {
  poc_id: string
  pre_auth_status: 'submitted' | 'approved' | 'denied'
  pre_auth_submit_date?: string
  pre_auth_date?: string
  pre_auth_id?: string
  authorized_no_session?: number
  auth_service_period?: string
  denial_date?: string
  denial_reason?: string
}

export interface UpdatePoCStatusResponse {
  message: string
  data: PoCDocument
}

export interface EditPoCRequest {
  poc_id: string
  poc_status: 'draft' | 'submitted'
  poc_doc: string // HTML content
  signature?: string | null
}

export interface EditPoCResponse {
  message: string
  data: PoCDocument
}

// PoC API functions
export const pocApi = {
  // Generate PoC using AI
  generatePoC: async (
    request: GeneratePoCRequest,
    config?: { signal?: AbortSignal }
  ): Promise<GeneratePoCResponse> => {
    const response = await api.post<GeneratePoCResponse>(
      '/notes/generate',
      request,
      {
        timeout: 0, // No timeout - wait indefinitely for AI generation
        signal: config?.signal,
      }
    )
    return response.data
  },

  // Save PoC (as draft or submitted)
  savePoC: async (request: SavePoCRequest): Promise<SavePoCResponse> => {
    const response = await api.post<SavePoCResponse>(
      '/sessions/save-poc',
      request
    )
    return response.data // Response already has the structure with message and data
  },

  // Certify PoC
  certifyPoC: async (
    request: CertifyPoCRequest
  ): Promise<CertifyPoCResponse> => {
    const response = await api.patch<CertifyPoCResponse>(
      '/sessions/certify-poc',
      request
    )
    return response.data
  },

  // Get PoC list for a contract
  getPoCList: async (contractId: string): Promise<PoCDocument[]> => {
    const response = await api.get<GetPoCListResponse>(
      `/sessions/get-session-poc/${contractId}`
    )
    return response.data.data || []
  },

  // Update PoC status (for preauthorization workflow)
  updatePoCStatus: async (
    request: UpdatePoCStatusRequest
  ): Promise<UpdatePoCStatusResponse> => {
    const response = await api.patch<UpdatePoCStatusResponse>(
      '/sessions/update-status',
      request
    )
    return response.data
  },

  // Edit PoC document
  editPoC: async (request: EditPoCRequest): Promise<EditPoCResponse> => {
    const response = await api.put<EditPoCResponse>(
      '/sessions/edit-poc',
      request
    )
    return response.data
  },
}

